import {
    Button,
    OutlinedCard,
    Stack,
} from '@myphx-engineering/phx-design-system';

import type { PathwayOptionConfig } from '../../../../configuration/shared';
import {
    useFillPathway,
    useFillPrescription,
} from '../../../../contexts/fill/use-fill-prescription';
import { useCardPricing } from '../../../../hooks/use-card-pricing';
import { useMedicationDisclaimers } from '../../../../hooks/use-medication-disclosures';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';

import { CardBullets } from './CardBullets';
import { CardHeader } from './CardHeader';
import { CardPrice } from './CardPrice';
import { resolveSubHeading } from './resolve-sub-heading';

interface PathwayOptionCardProps {
    option: PathwayOptionConfig;
}

export const PathwayOptionCard = ({ option }: PathwayOptionCardProps) => {
    const { drxId, medication } = useFillPrescription();
    const terms = useProgramTerms(medication, option.fillPathway);
    const {
        displayPrice,
        fallbackPrice,
        offer,
        fallbackDisclaimer,
        disclaimer,
        termsUrl,
    } = useCardPricing(option);

    const { originalPathway, pathway, subPathway } = useFillPathway();

    const disclaimers = useMedicationDisclaimers({
        headlinePriceType: option.headlinePriceType,
        medication,
        slot: 'path-select-card',
    });

    const subHeading = resolveSubHeading(option.content, terms);

    const testIdPrefix = `path-select-card-${option.id}`;

    return (
        <OutlinedCard
            pinFooter
            fullHeight
            data-testid={testIdPrefix}
            header={
                <CardHeader
                    content={option.content}
                    subHeading={subHeading}
                    testIdPrefix={testIdPrefix}
                />
            }
            footer={
                <Button
                    fullWidth
                    link
                    mt="md"
                    variant={option.content.buttonVariant ?? 'filled'}
                    data-testid={`${testIdPrefix}-button`}
                    to={{
                        pathname: getAbsoluteRoute(
                            option.destination === 'pharmacy-select'
                                ? 'fill.drxId.pharmacy.root'
                                : 'fill.drxId.root',
                            { drxId }
                        ),
                        search: `?pathway=${option.fillPathway}&previous-pathway=${subPathway ? `${pathway}|${subPathway}` : pathway}${
                            option.subPathway
                                ? `&sub-pathway=${option.subPathway}`
                                : ''
                        }${originalPathway ? `&original-pathway=${originalPathway}` : ''}`,
                    }}
                >
                    {option.content.button}
                </Button>
            }
        >
            <Stack gap="md">
                <CardPrice
                    content={option.content}
                    offer={offer}
                    displayPrice={displayPrice}
                    fallbackPrice={fallbackPrice}
                    fallbackDisclaimer={fallbackDisclaimer}
                    disclaimer={disclaimer}
                    disclaimerV2={disclaimers}
                    termsUrl={termsUrl}
                    testIdPrefix={testIdPrefix}
                />
                <CardBullets
                    content={option.content}
                    testIdPrefix={testIdPrefix}
                />
            </Stack>
        </OutlinedCard>
    );
};
